import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Download, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import {
  trackFormOpen,
  trackFormSubmitStart,
  trackFormSubmitSuccess,
  trackFormSubmitError,
  trackFormFieldInteraction,
} from "../utils/analytics";
import { trackCAPILead, trackCAPIInitiateCheckout } from "../utils/metaConversionsAPI";

// Environment variables from .env file
const SCRIPT_URL = process.env.REACT_APP_SCRIPT_URL || "";
const SECRET = process.env.REACT_APP_SECRET || "";

// Validate environment variables
if (!SCRIPT_URL || !SECRET) {
  console.error(
    "Missing environment variables. Please ensure REACT_APP_SCRIPT_URL and REACT_APP_SECRET are set in .env file"
  );
}

async function submitApplication(form, mode = "application") {
  // Check if environment variables are set
  if (!SCRIPT_URL || !SECRET) {
    throw new Error(
      "Configuration error: Missing required environment variables. Please contact support."
    );
  }
  const payload = {
    secret: SECRET,
    website: "", // honeypot (keep empty)
    fullName: form.fullName,
    email: form.email,
    phone: form.phone,
    source: window.location.href,
    action: mode === "download" ? "download_course_details" : "application",
  };

  // Add application-specific fields only for application mode
  if (mode === "application") {
    payload.city = form.city;
    payload.programme = form.programme;
    payload.ageRange = form.ageRange;
    payload.experience = form.priorExperience;
  }

  // Use FormData for POST to avoid CORS preflight issues
  const formData = new FormData();
  Object.keys(payload).forEach((key) => {
    formData.append(key, payload[key]);
  });

  try {
    const res = await fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors", // Bypass CORS - Google Apps Script will receive the data
      body: formData,
    });

    // With no-cors mode, we can't read the response
    // But the data will be submitted successfully
    return { ok: true, message: "Application submitted successfully!" };
  } catch (error) {
    // If there's a network error, it will be caught here
    throw new Error("Failed to submit application. Please try again.");
  }
}

export const ApplicationModal = ({
  isOpen,
  onClose,
  mode = "application",
  source = "unknown",
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    programme: "",
    ageRange: "",
    priorExperience: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const isDownloadMode = mode === "download";

  // Track form open
  useEffect(() => {
    if (isOpen) {
      trackFormOpen(source, mode);
      
      // Generate unique event ID for deduplication
      const eventId = `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Track Meta Pixel InitiateCheckout event (browser-side)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", {
          content_name: mode === "application" ? "Acting Programme Application" : "Course Details Download",
          content_category: "Application Form",
        }, { eventID: eventId });
      }

      // Track Meta Conversions API InitiateCheckout event (server-side) with same event ID
      trackCAPIInitiateCheckout(mode, eventId);
    }
  }, [isOpen, source, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Track form submission start
    trackFormSubmitStart(mode);

    try {
      await submitApplication(formData, mode);

      // Track successful submission
      trackFormSubmitSuccess(mode, formData);

      // Generate unique event ID for deduplication
      const leadEventId = `lead_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Track Meta Pixel Lead event (browser-side)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        if (mode === "application") {
          window.fbq("track", "Lead", {
            content_name: formData.programme,
            content_category: "Acting Programme Application",
            value: formData.programme.includes("Diploma") ? 236000 : 94400,
            currency: "INR",
          }, { eventID: leadEventId });
        } else if (mode === "download") {
          window.fbq("track", "Lead", {
            content_name: "Course Details Download",
            content_category: "Course Brochure",
          }, { eventID: leadEventId });
        }
      }

      // Track Meta Conversions API Lead event (server-side) with same event ID
      // This provides more reliable tracking and includes hashed user data
      trackCAPILead(formData, mode, leadEventId);

      // Close modal and reset form
      onClose();
      resetForm();

      // Redirect to thank you page with Google Ads conversion tracking
      const thankYouUrl = `/thank-you?mode=${mode}&source=${encodeURIComponent(
        source
      )}`;

      // Use gtagSendEvent if available (for Google Ads conversion tracking)
      // This delays navigation until the conversion event is sent
      if (
        typeof window !== "undefined" &&
        typeof window.gtagSendEvent === "function"
      ) {
        // gtagSendEvent will handle navigation via window.location
        window.gtagSendEvent(thankYouUrl);
      } else {
        // Fallback: Track conversion and navigate
        if (
          typeof window !== "undefined" &&
          typeof window.gtag === "function"
        ) {
          window.gtag("event", "ads_conversion_SUBMIT_LEAD_FORM_1", {
            event_timeout: 2000,
          });
        }
        // Use React Router navigate for client-side routing
        navigate(thankYouUrl);
      }
    } catch (error) {
      // Track submission error
      trackFormSubmitError(mode, error.message);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      city: "",
      programme: "",
      ageRange: "",
      priorExperience: "",
    });
  };

  const handleFieldFocus = (fieldName) => {
    trackFormFieldInteraction(fieldName, "focus");
  };

  const handleFieldChange = (fieldName) => {
    trackFormFieldInteraction(fieldName, "change");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <>
          <DialogHeader>
            {isDownloadMode ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <DialogTitle className="text-2xl font-bold text-gray-900">
                    Download Course Details
                  </DialogTitle>
                </div>
                <p className="text-gray-600 mt-2">
                  Please provide your details to download the complete course
                  information.
                </p>
              </>
            ) : (
              <>
                <DialogTitle className="text-3xl font-bold text-gray-900">
                  Apply Now - Limited Seats
                </DialogTitle>
                <p className="text-gray-600 mt-2">
                  Take the first step towards your acting career. Our admissions
                  team will contact you within 24 hours.
                </p>
              </>
            )}
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 mt-6">
            {/* Full Name */}
            <div>
              <Label
                htmlFor="fullName"
                className="text-base font-semibold text-gray-700"
              >
                Full Name *
              </Label>
              <Input
                id="fullName"
                required
                value={formData.fullName}
                onFocus={() => handleFieldFocus("fullName")}
                onChange={(e) => {
                  handleFieldChange("fullName");
                  setFormData({ ...formData, fullName: e.target.value });
                }}
                className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div>
              <Label
                htmlFor="email"
                className="text-base font-semibold text-gray-700"
              >
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onFocus={() => handleFieldFocus("email")}
                onChange={(e) => {
                  handleFieldChange("email");
                  setFormData({ ...formData, email: e.target.value });
                }}
                className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                placeholder="your@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <Label
                htmlFor="phone"
                className="text-base font-semibold text-gray-700"
              >
                Phone (WhatsApp) *
              </Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onFocus={() => handleFieldFocus("phone")}
                onChange={(e) => {
                  handleFieldChange("phone");
                  setFormData({ ...formData, phone: e.target.value });
                }}
                className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>

            {/* City - Only for application mode */}
            {!isDownloadMode && (
              <>
                <div>
                  <Label
                    htmlFor="city"
                    className="text-base font-semibold text-gray-700"
                  >
                    City *
                  </Label>
                  <Input
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Your city"
                  />
                </div>

                {/* Programme Selection */}
                <div>
                  <Label className="text-base font-semibold text-gray-700">
                    Select Programme *
                  </Label>
                  <Select
                    required
                    value={formData.programme}
                    onValueChange={(value) => {
                      handleFieldChange("programme");
                      setFormData({ ...formData, programme: value });
                    }}
                    onOpenChange={(open) => {
                      if (open) handleFieldFocus("programme");
                    }}
                  >
                    <SelectTrigger className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                      <SelectValue placeholder="Choose your programme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Diploma (Full-Time)">
                        Diploma (Full-Time) - Starts Nov 16th, 2026
                      </SelectItem>
                      <SelectItem value="Weekend (Part-Time)">
                        Weekend (Part-Time) - Starts 14th Nov, 2026
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Age Range */}
                <div>
                  <Label className="text-base font-semibold text-gray-700">
                    Age Range *
                  </Label>
                  <Select
                    required
                    value={formData.ageRange}
                    onValueChange={(value) =>
                      setFormData({ ...formData, ageRange: value })
                    }
                  >
                    <SelectTrigger className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                      <SelectValue placeholder="Select your age range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-18">Under 18</SelectItem>
                      <SelectItem value="18-25">18-25</SelectItem>
                      <SelectItem value="26-35">26-35</SelectItem>
                      <SelectItem value="36-45">36-45</SelectItem>
                      <SelectItem value="above-45">Above 45</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Prior Experience */}
                <div>
                  <Label className="text-base font-semibold text-gray-700">
                    Prior Acting Experience *
                  </Label>
                  <Select
                    required
                    value={formData.priorExperience}
                    onValueChange={(value) =>
                      setFormData({ ...formData, priorExperience: value })
                    }
                  >
                    <SelectTrigger className="mt-2 h-12 text-base border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                      <SelectValue placeholder="Have you acted before?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">
                        Yes, I have acting experience
                      </SelectItem>
                      <SelectItem value="no">No, I'm a beginner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 mt-6">
              {isDownloadMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className={`${
                  isDownloadMode ? "flex-1" : "w-full"
                } bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white text-lg font-bold py-6 shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 transition-all duration-300`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isDownloadMode ? (
                      <>
                        <Download className="w-5 h-5" />
                        Download PDF
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Application
                      </>
                    )}
                  </span>
                )}
              </Button>
            </div>

            <p className="text-sm text-gray-500 text-center mt-4">
              🔒 Your information is secure. We'll contact you via WhatsApp/Call
              within 24 hours.
            </p>
          </form>
        </>
      </DialogContent>
    </Dialog>
  );
};
